import { Subtitle, SubtitleSection } from "chq";

import { ElementState } from "@creatomate/preview";
import updateNextElementsSubName from "./updateNextElementsSubName";
import createSubtitleElement from "./createSubtitleElement";
import isSubtitleText from "./isSubtitleText";

export const getPercentageofSentence = (sentence: string, percentage: number) => {
  if (percentage <= 0) {
    return {
      cutOffSentence: "",
      restOfTheSentence: sentence
    };
  }

  // Split the sentence into individual words
  const words = sentence.split(" ");
  // Calculate the percentage of the total number of words and round it up
  const percent = Math.ceil(words.length * (percentage / 100));
  // Extract the first words and join them back into a sentence
  const cutOffSentence = words.slice(0, percent).join(" ");
  // Extract the rest of the words and join them back into a sentence
  const restOfTheSentence = words.slice(percent).join(" ");

  return {
    cutOffSentence,
    restOfTheSentence
  };
};

const getSplittedSentence = (
  text: string, sectionEndtime: number, sectionStartTime: number, currentTime: number
) => {
  const sectionDuration = sectionEndtime - sectionStartTime;
  const currentTimeInSection = currentTime - sectionStartTime;
  const percentageInSection = (currentTimeInSection * 100) / sectionDuration;

  return getPercentageofSentence(text, percentageInSection);
};

const splitSection = (
  section: SubtitleSection,
  currentTime: number,
  organization: string,
  elements: Record<string, any>[],
  rendererElements: ElementState[],
  updatedSections: SubtitleSection[],
  initialElements: Record<string, any>[]
): Record<string, any>[] => {
  const { cutOffSentence, restOfTheSentence } = getSplittedSentence(
    section.text, section.endTime, section.startTime, currentTime
  );

  const subtitlesTrack = elements.find(element => isSubtitleText(element.name))?.track;

  updatedSections.push({
    ...section,
    text: cutOffSentence,
    endTime: currentTime,
    originalText: cutOffSentence,
    originalEndTime: currentTime,
    index: updatedSections.length
  });

  let creatomateId;
  const newElements = [...initialElements];
  if (section.creatomateId) {
    const elementToUpdateIndex = elements.findIndex(element => element.id === section.creatomateId);
    newElements[elementToUpdateIndex] = {
      ...elements[elementToUpdateIndex],
      text: cutOffSentence,
      duration: currentTime - section.startTime
    };

    const newSection = createSubtitleElement(
      restOfTheSentence,
      updatedSections.length,
      currentTime + 0.01,
      section.endTime,
      subtitlesTrack,
      organization,
      rendererElements.find(e => e.source.id === elements[elementToUpdateIndex].id),
      false
    );
    creatomateId = newSection.id;
    newElements.push(newSection);
  }

  updatedSections.push({
    ...section,
    creatomateId,
    startTime: currentTime + 0.01,
    originalStartTime: currentTime + 0.01,
    originalText: restOfTheSentence,
    text: restOfTheSentence,
    index: updatedSections.length
  });
  return newElements;
};

const processSections = (
  subtitle: Subtitle,
  currentTime: number,
  organization: string,
  elements: Record<string, any>[],
  rendererElements: ElementState[],
  initialElements: Record<string, any>[]
): { updatedSections: SubtitleSection[]; newElements: Record<string, any>[] } => {
  const updatedSections: SubtitleSection[] = [];
  let newIndex;
  let newElements = [...initialElements];
  if (!subtitle.sections) return { updatedSections, newElements };

  for (let sectionIndex = 0; sectionIndex < subtitle.sections.length; sectionIndex += 1) {
    const section = subtitle.sections[sectionIndex];

    const isCurrentTimeInRange = section.startTime <= currentTime && section.endTime >= currentTime;
    if (isCurrentTimeInRange) {
      newElements = splitSection(
        section, currentTime, organization, elements, rendererElements, updatedSections, newElements
      );
      newIndex = updatedSections.length;
    } else {
      updatedSections.push({ ...section, index: updatedSections.length });
    }
    // We need to update the name for the other elements in creatomate
    if (newIndex && sectionIndex >= newIndex) {
      const updatedElement = updateNextElementsSubName(sectionIndex, section, rendererElements);
      const elementToUpdateIndex = elements.findIndex(element => element.id === updatedElement.id);
      newElements[elementToUpdateIndex] = { ...updatedElement };
    }
  }
  return { updatedSections, newElements };
};

const splitSubtitle = async (
  subtitles: Subtitle[],
  currentTime: number,
  onUpdateSubtitles: (newData: Record<string, any>) => void,
  organization: string,
  elements: Record<string, any>[] = [],
  rendererElements: ElementState[] = []
): Promise<Record<string, any>> => {
  let newElements = [...elements];
  const updatedSubtitles: Subtitle[] = [];
  for (let subtitleIndex = 0; subtitleIndex < subtitles.length; subtitleIndex += 1) {
    const subtitle = subtitles[subtitleIndex];
    if (!subtitle.deleted) {
      const { updatedSections, newElements: updatedNewElements } = processSections(
        subtitle, currentTime, organization, elements, rendererElements, newElements
      );
      updatedSubtitles.push({ ...subtitle, sections: updatedSections });
      newElements = updatedNewElements;
    }
  }
  onUpdateSubtitles({ subtitles: updatedSubtitles });
  return newElements;
};

export default splitSubtitle;
